import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Textarea } from '@/components/ui/Textarea'
import { Avatar } from '@/components/ui/Avatar'
import { TimeAgo } from '@/components/TimeAgo'
import { type RowAnchor } from './rowAnchor'
import { type RequestComment } from './revisionRequest'

// Under one commented row: the comments so far (edit/delete on the viewer's
// own while the request is a draft), an "Add another comment" link, and the
// GitHub-style composer (Cancel / Comment) when open. Renders nothing when
// there are no comments and the composer is closed.
export default function RowCommentThread({
  anchor, comments, composerOpen, readOnly = false, onOpenComposer, onCloseComposer, onCreate, onUpdate, onDelete,
}: {
  anchor: RowAnchor
  comments: RequestComment[]
  composerOpen: boolean
  readOnly?: boolean
  onOpenComposer: () => void
  onCloseComposer: () => void
  onCreate: (body: string) => void
  onUpdate: (id: string, body: string) => void
  onDelete: (id: string) => void
}) {
  if (comments.length === 0 && !composerOpen) return null
  return (
    <div className="flex flex-col gap-2 border-l-2 border-primary/40 bg-primary/5 px-4 py-3" data-row-thread={`${anchor.section}:${anchor.anchor_id}`}>
      {comments.map((c) => (
        <CommentRow key={c.id} comment={c} readOnly={readOnly} onUpdate={onUpdate} onDelete={onDelete} />
      ))}
      {!readOnly && !composerOpen && comments.length > 0 && (
        <button type="button" onClick={onOpenComposer} className="self-start text-xs text-link hover:underline">
          Add another comment
        </button>
      )}
      {!readOnly && composerOpen && (
        <Composer label={anchor.label} onCancel={onCloseComposer} onSubmit={onCreate} />
      )}
    </div>
  )
}

// `withContext` adds the kind chip and "on <cell>"; a per-cell tab already
// names the cell, so it leaves them out.
export function CommentRow({ comment, readOnly, onUpdate, onDelete, withContext = true }: {
  comment: RequestComment; readOnly: boolean
  onUpdate: (id: string, body: string) => void; onDelete: (id: string) => void
  withContext?: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [body, setBody] = useState(comment.body)
  return (
    <div className="flex items-start gap-2 text-sm">
      <Avatar size="sm" name={comment.author} avatarUrl={comment.author_avatar_url} aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">{comment.author}</span> · <TimeAgo at={comment.at} />
          {withContext && (
            <>
              <span className="ml-1.5 rounded border border-border bg-background px-1 py-px text-[10px] font-medium text-foreground">
                {comment.kind === 'review' ? 'Review comment' : 'Revision request'}
              </span>
              {comment.cell_label && <> · on <span className="font-medium text-foreground">{comment.cell_label}</span></>}
            </>
          )}
          {comment.outdated && (
            <span className="ml-1.5 rounded border border-border bg-muted px-1 py-px text-[10px] font-semibold uppercase tracking-wide text-foreground">Outdated</span>
          )}
        </div>
        {editing ? (
          <div className="mt-1 flex flex-col gap-2">
            <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => { setBody(comment.body); setEditing(false) }}>Cancel</Button>
              <Button size="sm" disabled={!body.trim()} onClick={() => { onUpdate(comment.id, body.trim()); setEditing(false) }}>Save</Button>
            </div>
          </div>
        ) : (
          <p className="whitespace-pre-line text-[13px] leading-relaxed text-foreground">{comment.body}</p>
        )}
        {!readOnly && comment.mine && !editing && (
          <div className="mt-0.5 flex gap-3 text-xs">
            <button type="button" onClick={() => { setBody(comment.body); setEditing(true) }} className="text-link hover:underline">Edit</button>
            <button type="button" onClick={() => onDelete(comment.id)} className="text-muted-foreground hover:underline">Delete</button>
          </div>
        )}
      </div>
    </div>
  )
}

function Composer({ label, onCancel, onSubmit }: { label: string; onCancel: () => void; onSubmit: (body: string) => void }) {
  const [body, setBody] = useState('')
  return (
    <div className="flex flex-col gap-2 rounded-md border bg-background p-3" style={{ borderColor: 'var(--border)' }}>
      <p className="text-sm font-semibold text-foreground">Add a comment on {label}</p>
      <Textarea
        autoFocus
        rows={3}
        className="focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-chart-muted"
        placeholder="Leave a comment"
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button size="sm" disabled={!body.trim()} onClick={() => onSubmit(body.trim())}>Comment</Button>
      </div>
    </div>
  )
}
